import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Maximize2, BedDouble, Building } from 'lucide-react';

const statusStyles = {
  'Neu': 'badge-neu',
  'Verfügbar': 'badge-verfugbar',
  'In Verhandlung': 'badge-verhandlung',
  'Verkauft': 'badge-verkauft',
  'Vermietet': 'badge-vermietet',
};

export default function PropertyCard({ property, index = 0, isReference = false }) {
  const {
    id,
    title,
    property_type,
    location,
    price,
    price_on_request,
    area_sqm,
    rooms,
    floor,
    images,
    status,
    transaction_type,
    days_on_market,
    testimonial,
    testimonial_author
  } = property;

  const formatPrice = (price, transaction_type, price_on_request) => {
    if (price_on_request) return 'Preis auf Anfrage';
    if (!price) return 'Preis auf Anfrage';
    const formatted = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price);
    return transaction_type === 'Mieten' ? `${formatted}/Monat` : formatted;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="group"
    >
      <div className={`card-luxury overflow-hidden ${isReference ? 'relative' : ''}`}>
        {/* Image */}
        <div className="relative h-56 overflow-hidden">
          <img
            src={images?.[0] || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=800&q=80'}
            alt={title}
            className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${isReference ? 'grayscale-[30%]' : ''}`}
            loading="lazy"
          />
          {/* Status Badge */}
          <div className="absolute top-4 left-4">
            <span className={`px-3 py-1 text-xs font-medium ${statusStyles[status] || 'badge-verfugbar'}`}>
              {status}
            </span>
          </div>
          {/* Reference overlay */}
          {isReference && (
            <div className="absolute inset-0 bg-[#131d30]/40 flex items-center justify-center">
              <div className="bg-[#d1af6a] text-[#0a1120] px-4 py-2 font-accent text-xs tracking-widest">
                {status === 'Verkauft' ? 'ERFOLGREICH VERKAUFT' : 'ERFOLGREICH VERMIETET'}
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Type */}
          <span className="text-[#c8c6c4] text-xs font-medium uppercase tracking-wider">
            {property_type}
          </span>

          {/* Title */}
          <h3 className="font-subheading text-lg text-white mt-2 mb-3 group-hover:text-[#d1af6a] transition-colors line-clamp-2">
            {title}
          </h3>

          {/* Location */}
          <div className="flex items-center gap-2 text-[#c8c6c4] text-sm mb-4">
            <MapPin className="w-4 h-4 text-[#d1af6a]" />
            {location}
          </div>

          {/* Features */}
          <div className="flex items-center gap-4 text-[#c8c6c4] text-sm mb-4">
            <div className="flex items-center gap-1">
              <Maximize2 className="w-4 h-4 text-[#d1af6a]" />
              {area_sqm} m²
            </div>
            {rooms && (
              <div className="flex items-center gap-1">
                <BedDouble className="w-4 h-4 text-[#d1af6a]" />
                {rooms} Zimmer
              </div>
            )}
            {floor && (
              <div className="flex items-center gap-1">
                <Building className="w-4 h-4 text-[#d1af6a]" />
                {floor}
              </div>
            )}
          </div>

          {/* Reference specific info */}
          {isReference && days_on_market && (
            <p className="text-[#d1af6a] text-sm mb-4">
              Vermittelt in {days_on_market} Tagen
            </p>
          )}

          {/* Testimonial */}
          {isReference && testimonial && (
            <blockquote className="border-l-2 border-[#d1af6a] pl-4 mb-4">
              <p className="text-[#c8c6c4] text-sm italic">"{testimonial}"</p>
              {testimonial_author && (
                <cite className="text-[#d1af6a] text-xs not-italic mt-2 block">
                  – {testimonial_author}
                </cite>
              )}
            </blockquote>
          )}

          {/* Price & CTA */}
          <div className="flex items-center justify-between pt-4 border-t border-[#d1af6a]/20">
            <span className="font-display text-xl text-white">
              {isReference ? (
                <span className="text-[#c8c6c4] text-sm">{status}</span>
              ) : (
                formatPrice(price, transaction_type, price_on_request)
              )}
            </span>
            {!isReference && (
              <Link
                to={`/immobilien/${id}`}
                className="text-[#d1af6a] text-sm font-medium hover:underline"
                data-testid={`property-card-link-${id}`}
              >
                Details ansehen
              </Link>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
